import Cookies from "js-cookie";
import Api from "@/service/api-web";
import axios from "axios";

export const keycloakConfig = {
  url:
    process.env.VUE_APP_KEYCLOAK_URL || "https://seu-keycloak-domain.com/auth",
  realm: process.env.VUE_APP_KEYCLOAK_REALM || "seu-realm",
  clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID || "backoffice-client",
  redirectUri: `${window.location.origin}/auth/callback/keycloak`
};

export class KeycloakUtils {
  static _refreshInProgress = false;
  static _refreshPromise = null;
  static refreshTimer = null;

  static generateState() {
    return (
      Math.random()
        .toString(36)
        .substring(2, 15) +
      Math.random()
        .toString(36)
        .substring(2, 15)
    );
  }

  static redirectToKeycloak(email = "") {
    const state = this.generateState();
    localStorage.setItem("keycloak_state", state);

    const params = new URLSearchParams({
      client_id: keycloakConfig.clientId,
      redirect_uri: keycloakConfig.redirectUri,
      response_type: "code",
      scope: "openid email profile",
      state: state,
      ...(email && { login_hint: email })
    });

    const authUrl = `${keycloakConfig.url}/realms/${
      keycloakConfig.realm
    }/protocol/openid-connect/auth?${params.toString()}`;

    window.location.href = authUrl;
  }

  static handleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const error = urlParams.get("error");

    const savedState = localStorage.getItem("keycloak_state");
    if (state !== savedState) {
      throw new Error("Estado inválido");
    }

    if (error) {
      throw new Error(`Erro no Keycloak: ${error}`);
    }

    return { code, state };
  }

  static async isTokenValid() {
    try {
      const token = Cookies.get("keycloak_token");
      if (!token) return false;
      await axios.get(
        `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/userinfo`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      return true;
    } catch (e) {
      return false;
    }
  }

  static async refreshToken() {
    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    this._refreshPromise = this._performRefresh();

    try {
      const result = await this._refreshPromise;
      return result;
    } finally {
      this._refreshPromise = null;
    }
  }

  static async _performRefresh() {
    try {
      const refreshToken = Cookies.get("keycloak_refresh_token");

      if (!refreshToken) {
        throw new Error("Refresh token não encontrado");
      }

      this._refreshInProgress = true;

      const response = await Api.api.post("/refresh-token", {
        refreshToken: refreshToken
      });

      const {
        authorization,
        authorization1,
        authorization2,
        authorization3,
        keycloakToken: newKeycloakToken,
        refreshToken: newRefreshToken,
        expiresIn,
        refreshExpiresIn
      } = response.data;

      const cookieConfig = { expires: 365, secure: true };
      Cookies.set("authorization", authorization, cookieConfig);
      Cookies.set("authorization1", authorization1, cookieConfig);
      Cookies.set("authorization2", authorization2, cookieConfig);
      Cookies.set("authorization3", authorization3, cookieConfig);
      Cookies.set("keycloak_token", newKeycloakToken, cookieConfig);

      if (newRefreshToken) {
        Cookies.set("keycloak_refresh_token", newRefreshToken, cookieConfig);
      }

      if (expiresIn) {
        const expiryTime = Date.now() + expiresIn * 1000;
        Cookies.set("token_expires_at", expiryTime.toString(), cookieConfig);
        this.startTokenRefreshTimer(expiresIn);
      }

      return {
        success: true,
        authorization,
        authorization1,
        authorization2,
        authorization3,
        keycloakToken: newKeycloakToken,
        refreshToken: newRefreshToken,
        expiresIn,
        refreshExpiresIn
      };
    } catch (error) {
      if (error.response?.data?.requiresLogin) {
        this.clearAllTokens();
        throw new Error("Sessão expirada. Faça login novamente.");
      }
      throw error;
    } finally {
      this._refreshInProgress = false;
    }
  }

  static isTokenNearExpiry(expiresInSeconds, thresholdMinutes = 5) {
    const thresholdSeconds = thresholdMinutes * 60;
    return expiresInSeconds <= thresholdSeconds;
  }

  static checkStoredTokenExpiry() {
    const storedExpiry = Cookies.get("token_expires_at");
    if (!storedExpiry) {
      const hasTokens =
        Cookies.get("keycloak_token") && Cookies.get("keycloak_refresh_token");
      if (hasTokens) {
        return { needsRefresh: false, timeToExpiry: 1800000, expired: false };
      }
      return { needsRefresh: false, timeToExpiry: null };
    }

    const expiryTime = parseInt(storedExpiry);
    const currentTime = Date.now();
    const timeToExpiry = expiryTime - currentTime;
    const thresholdTime = 5 * 60 * 1000;

    return {
      needsRefresh: timeToExpiry <= thresholdTime,
      timeToExpiry: Math.max(timeToExpiry, 0),
      expired: timeToExpiry <= 0
    };
  }

  static startTokenRefreshTimer(expiresInSeconds) {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const refreshTimeSeconds = Math.max(expiresInSeconds - 120, 60);
    const refreshTimeMs = refreshTimeSeconds * 1000;

    this.refreshTimer = setTimeout(async () => {
      try {
        await this.refreshToken();
      } catch (error) {
        if (!error.message.includes("Sessão expirada")) {
          setTimeout(async () => {
            try {
              await this.refreshToken();
            } catch (retryError) {
              this.clearAllTokens();
              if (window.location.pathname !== "/login") {
                window.location.href = "/login";
              }
            }
          }, 30000);
        } else {
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
      }
    }, refreshTimeMs);
  }

  static clearAllTokens() {
    Cookies.remove("authorization");
    Cookies.remove("authorization1");
    Cookies.remove("authorization2");
    Cookies.remove("authorization3");
    Cookies.remove("keycloak_token");
    Cookies.remove("keycloak_refresh_token");
    Cookies.remove("token_expires_at");

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  static setCookie(name, value, options) {
    Cookies.set(name, value, options);
  }

  static getCookie(name) {
    return Cookies.get(name);
  }

  static async initializeTokenCheck() {
    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      const hasBasicTokens =
        Cookies.get("keycloak_token") && Cookies.get("keycloak_refresh_token");

      if (!hasBasicTokens) {
        return { initialized: false };
      }

      const hasAuthTokens =
        Cookies.get("authorization") ||
        Cookies.get("authorization1") ||
        Cookies.get("authorization2") ||
        Cookies.get("authorization3");

      if (!hasAuthTokens) {
        return { initialized: false, missingAuthTokens: true };
      }

      const tokenStatus = this.checkStoredTokenExpiry();

      if (tokenStatus.expired || tokenStatus.timeToExpiry === null) {
        try {
          const result = await this.refreshToken();
          if (result.success && result.expiresIn) {
            this.startTokenRefreshTimer(result.expiresIn);
            return { initialized: true, refreshed: true };
          }
        } catch (error) {
          return { initialized: false, expired: true };
        }
      } else if (tokenStatus.needsRefresh) {
        try {
          const result = await this.refreshToken();
          if (result.success && result.expiresIn) {
            this.startTokenRefreshTimer(result.expiresIn);
            return { initialized: true, refreshed: true };
          }
        } catch (error) {
          return { initialized: false, error };
        }
      }

      const remainingSeconds = Math.floor(tokenStatus.timeToExpiry / 1000);
      this.startTokenRefreshTimer(remainingSeconds);

      return { initialized: true, timeToExpiry: tokenStatus.timeToExpiry };
    } catch (error) {
      return { initialized: false, error };
    }
  }
}
